import React, { ReactElement } from "react";
import { createPermissionsQueryOptions, createRoleIdQueryOptions } from "../services/rolesPermissions/rolesPermission.options";
import { createMergeTemplatePermissionsOptions } from "@/services/template/template.options";
import { QueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { AuthContext, handleUnauthorizedError, useAuth } from "@/lib/auth";
import { createUserQueryOptions } from "@/services/auth/auth.options";
import { setupInterceptors } from "@/services/api.service";

interface PermissionsProps {
  target: string;
  children: ReactElement;
  disableMode?: boolean
}

interface CheckResourceAccessProps {
  queryClient: QueryClient
  pathname: string
  permissions: any
  auth: AuthContext
}

export const checkResourceAccess = async ({
  queryClient,
  pathname,
  permissions,
  auth
}: CheckResourceAccessProps): Promise<boolean> => {
  try {
    const [permissions, mergeTemplatePermission, user] = await Promise.all([
      queryClient.fetchQuery(createPermissionsQueryOptions(auth.checkAuthStatus)),
      queryClient.fetchQuery(createMergeTemplatePermissionsOptions()),
      // queryClient.fetchQuery(createRoleIdQueryOptions()),
      queryClient.fetchQuery(createUserQueryOptions({ ticketid: 0, customerid: 0, limittocustomer: false }))
    ])

    auth.updateUser(user)
    console.log('permissions')
    console.log(pathname)
    console.log(permissions)
    console.log(mergeTemplatePermission)
    // console.log(roleId)
    console.log(user)


    return (
      // adding data object for same data access as from useQuery
      handleAccess(pathname, { data: permissions }, { data: mergeTemplatePermission.permissions })
    )
  } catch (error) {
    console.warn('Permissions or mergeTemplatePermission or roleId is null')
    // handleUnauthorizedError(queryClient)
    console.error('Error checking resource access:', error)
    return false
  }
}

export const handleAccess = (target: string, permissions?: any, mergeTemplatePermission?: any): boolean => {

  // console.log(permissions)
  // console.log(permissions?.data?.permissions)

  if (!permissions?.data?.permissions) return false;

  switch (target) {
    case "ticketDetailSave":
      return permissions?.data?.permissions["Ticket Editor"];

    case "ticketDetailDelete":
      return permissions?.data?.permissions["Ticket Deleter"];

    case "createTicket":
      return permissions?.data?.permissions["Ticket Creator"];

    case "canChangeStatus":
      return (
        permissions?.data?.permissions["Ticket Editor"] &&
        permissions?.data?.permissions["Ticket Mover"]
      )

    case "canChangeAssignee":
      return (
        permissions?.data?.permissions["Ticket Editor"] &&
        permissions?.data?.permissions["Ticket Assign-other"]
      )

    case "canDeleteAttachment":
      return (
        //Self is managed inside of Attachments.tsx
        permissions?.data?.roleid === 10 || // Drop Supervisor
        permissions?.data?.permissions["Customer Administrator"]
      )

    // NavBar below

    case "Tickets":
      return true

    case "/":
      return true

    case "/tickets":
      return true

    case "/ticket":
      return true

    case "Status Editor":
      return (
        permissions?.data?.permissions["System Manager"] ||
        permissions?.data?.permissions["Customer Administrator"]
      )

    case '/status-editor':
      console.log('here')
      return (
        permissions?.data?.permissions["System Manager"] ||
        permissions?.data?.permissions["Customer Administrator"]
      )

    case "Type Editor":
      return (
        permissions?.data?.permissions["System Manager"] ||
        permissions?.data?.permissions["Customer Administrator"] ||
        permissions?.data?.permissions["Mass Update Manager"]
      )

    case "/type-editor":
      return (
        permissions?.data?.permissions["System Manager"] ||
        permissions?.data?.permissions["Customer Administrator"] ||
        permissions?.data?.permissions["Mass Update Manager"]
      )

    case "Custom Fields":
      return (
        permissions?.data?.permissions["System Manager"] ||
        permissions?.data?.permissions["Customer Administrator"]
      )

    case "/custom-fields":
      return (
        permissions?.data?.permissions["System Manager"] ||
        permissions?.data?.permissions["Customer Administrator"]
      )

    case "Field Groups":
      return (
        permissions?.data?.permissions["System Manager"] ||
        permissions?.data?.permissions["Customer Administrator"]
      )

    case "/field-groups":
      return (
        permissions?.data?.permissions["System Manager"] ||
        permissions?.data?.permissions["Customer Administrator"]
      )

    case "Customers":
      return (
        permissions?.data?.permissions["System Manager"] ||
        permissions?.data?.permissions["Customer Administrator"] ||
        permissions?.data?.permissions["Customer Manager"] ||
        permissions?.data?.permissions["All-seer"]
      )

    case "/customers":
      return (
        permissions?.data?.permissions["System Manager"] ||
        permissions?.data?.permissions["Customer Administrator"] ||
        permissions?.data?.permissions["Customer Manager"] ||
        permissions?.data?.permissions["All-seer"]
      )

    case "Users":
      return (
        permissions?.data?.permissions["System Manager"] ||
        permissions?.data?.permissions["Customer Administrator"]
      )

    case "/users":
      return (
        permissions?.data?.permissions["System Manager"] ||
        permissions?.data?.permissions["Customer Administrator"]
      )

    case "Roles & Permissions":
      return (
        permissions?.data?.permissions["System Manager"] ||
        permissions?.data?.permissions["Customer Administrator"]
      )

    case "/permissions":
      return (
        permissions?.data?.permissions["System Manager"] ||
        permissions?.data?.permissions["Customer Administrator"]
      )

    case "Workflow Rules":
      return (
        permissions?.data?.permissions["System Manager"] ||
        permissions?.data?.permissions["Customer Administrator"]
      )

    case "/workflow-rules":
      return (
        permissions?.data?.permissions["System Manager"] ||
        permissions?.data?.permissions["Customer Administrator"]
      )

    // case "Add Workflow Rule":
    //   return (
    //     permissions?.data?.permissions["System Manager"] ||
    //     permissions?.data?.permissions["Customer Administrator"]
    //   )

    case "Templates":
      return mergeTemplatePermission?.data?.MergeTemplateAccess

    case "/templates":
      return mergeTemplatePermission?.data?.MergeTemplateAccess

    case "Reports":
      return (
        permissions?.data?.permissions["System Manager"] ||
        permissions?.data?.permissions["Customer Administrator"] ||
        permissions?.data?.permissions["Report Executor"] ||
        permissions?.data?.permissions["Mass Update Manager"]
      )

    case "/reports":
      return (
        permissions?.data?.permissions["System Manager"] ||
        permissions?.data?.permissions["Customer Administrator"] ||
        permissions?.data?.permissions["Report Executor"] ||
        permissions?.data?.permissions["Mass Update Manager"]
      )

    case "Batch Updates":
      return (
        permissions?.data?.permissions["System Manager"] ||
        permissions?.data?.permissions["Customer Administrator"] ||
        permissions?.data?.permissions["Mass Update Manager"]
      )

    case "/batch-updates":
      return (
        permissions?.data?.permissions["System Manager"] ||
        permissions?.data?.permissions["Customer Administrator"] ||
        permissions?.data?.permissions["Mass Update Manager"]
      )

    case "User Profile":
      return true

    case "/user-profile":
      return true

    case "Release Notes":
      return true

    case "/release-notes":
      return true

    case "Sign Out":
      return true

    default:
      return false;
  }
};

const Permissions = ({ target, children, disableMode = false }: PermissionsProps) => {

  const auth = useAuth()

  const permissions = useSuspenseQuery(createPermissionsQueryOptions(auth.checkAuthStatus));
  const mergeTemplatePermission = useSuspenseQuery(createMergeTemplatePermissionsOptions());
  // const roleIdPermission = useSuspenseQuery(createRoleIdQueryOptions());
  const hasAccess = handleAccess(target, permissions, mergeTemplatePermission);

  if (disableMode) {
    return React.cloneElement(children, {
      disabled: !hasAccess || children.props.disabled, // Keep the original `disabled` prop
    });
  }

  return hasAccess ? <>{children}</> : null;
};

export default Permissions;